import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  SimpleGrid, VStack, Text, Divider, useToast, HStack, Checkbox, Badge,
  IconButton, Tabs, TabList, Tab, TabPanels, TabPanel, NumberInput,
  NumberInputField, CheckboxGroup, Stack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdAdd, MdDelete } from 'react-icons/md';
import type { Detencion, ControlMedico, MedicamentoAdmin, Tatuaje, Cicatriz, Piercing, CondicionPreexistente } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  detencion: Detencion | null;
  onGuardado: () => void;
}

const EMPTY_CM: Partial<ControlMedico> = {
  tipoSangre: '',
  complexion: 'Media',
  estadoConciencia: 'Consciente',
  nivelCooperacion: 'Cooperativo',
  habla: 'Normal',
  nivelRiesgo: 'Bajo',
};

export default function ModalControlMedico({ isOpen, onClose, detencion, onGuardado }: Props) {
  const [form, setForm] = useState<Partial<ControlMedico>>(EMPTY_CM);
  const [existente, setExistente] = useState<ControlMedico | null>(null);
  const [tatuajes, setTatuajes] = useState<Tatuaje[]>([]);
  const [cicatrices, setCicatrices] = useState<Cicatriz[]>([]);
  const [piercings, setPiercings] = useState<Piercing[]>([]);
  const [condiciones, setCondiciones] = useState<CondicionPreexistente>({});
  const [medicamentos, setMedicamentos] = useState<MedicamentoAdmin[]>([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const set = (k: keyof ControlMedico, v: any) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!isOpen || !detencion?.id) return;
    juzgadoApi.obtenerControlMedico(detencion.id)
      .then(cm => {
        setExistente(cm);
        setForm(cm);
        setTatuajes(cm.tatuajesJson ? JSON.parse(cm.tatuajesJson) : []);
        setCicatrices(cm.cicatriciesJson ? JSON.parse(cm.cicatriciesJson) : []);
        setPiercings(cm.piercingsJson ? JSON.parse(cm.piercingsJson) : []);
        setCondiciones(cm.condicionesJson ? JSON.parse(cm.condicionesJson) : {});
        setMedicamentos(cm.medicamentosAdminJson ? JSON.parse(cm.medicamentosAdminJson) : []);
      })
      .catch(() => {
        setExistente(null);
        setForm({ ...EMPTY_CM });
        setTatuajes([]);
        setCicatrices([]);
        setPiercings([]);
        setCondiciones({});
        setMedicamentos([]);
      });
  }, [isOpen, detencion]);

  const agregarTatuaje = () => setTatuajes(prev => [...prev, { ubicacion: '', descripcion: '', color: '', tamano: '' }]);
  const agregarCicatriz = () => setCicatrices(prev => [...prev, { ubicacion: '', tipo: 'corte', tamano: '' }]);
  const agregarPiercing = () => setPiercings(prev => [...prev, { ubicacion: 'Oreja' }]);
  const agregarMed = () => setMedicamentos(prev => [...prev, { nombre: '', tipo: 'Analgésico', tipoOtro: '', dosis: '', via: 'Oral', hora: '', responsable: '', motivo: '' }]);

  const handleGuardar = async () => {
    if (!detencion) return;
    if (!form.nivelRiesgo || !form.motivoRiesgo) {
      return toast({ title: 'Nivel de riesgo y motivo son requeridos', status: 'warning' });
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tatuajes,
        cicatrices,
        piercings,
        condiciones,
        medicamentosAdmin: medicamentos,
      };
      if (existente) {
        await juzgadoApi.actualizarControlMedico(existente.id, payload);
      } else {
        await juzgadoApi.crearControlMedico(detencion.id, payload);
      }
      toast({ title: 'Control médico guardado', status: 'success' });
      onGuardado();
      onClose();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Control Médico — {detencion?.folio}
          {existente && <Badge ml={2} colorScheme="green">Registrado</Badge>}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody pt={4}>
          <Tabs colorScheme="red" variant="enclosed">
            <TabList>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Biomédicos</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Rasgos</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Señales</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Neurológico</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Signos</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Condiciones</Tab>
              <Tab _selected={{ color: '#5b182b', borderColor: '#5b182b' }}>Medicamentos</Tab>
            </TabList>
            <TabPanels>

              {/* ── Biomédicos ────────────────────────────────────────── */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Tipo de sangre</FormLabel>
                    <Select value={form.tipoSangre ?? ''} onChange={e => set('tipoSangre', e.target.value)} borderColor="#bb8c52">
                      <option value="">Desconocido</option>
                      {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Estatura (cm)</FormLabel>
                    <Input type="number" value={form.estatura ?? ''} onChange={e => set('estatura', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Peso (kg)</FormLabel>
                    <Input type="number" value={form.peso ?? ''} onChange={e => set('peso', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </SimpleGrid>
                <Divider my={4} />
                <Text fontWeight="semibold" color="#5b182b" mb={2}>Riesgo Médico Inicial</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Nivel de riesgo</FormLabel>
                    <Select value={form.nivelRiesgo ?? ''} onChange={e => set('nivelRiesgo', e.target.value)} borderColor="#bb8c52">
                      <option value="Bajo">Bajo</option>
                      <option value="Medio">Medio</option>
                      <option value="Alto">Alto</option>
                      <option value="Crítico">Crítico</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Motivo de clasificación</FormLabel>
                    <Textarea value={form.motivoRiesgo ?? ''} onChange={e => set('motivoRiesgo', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </SimpleGrid>
                <FormControl mt={3}>
                  <FormLabel fontSize="sm">Médico responsable</FormLabel>
                  <Input value={form.medicoResponsable ?? ''} onChange={e => set('medicoResponsable', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
              </TabPanel>

              {/* ── Rasgos generales ─────────────────────────────────── */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Complexión</FormLabel>
                    <Select value={form.complexion ?? ''} onChange={e => set('complexion', e.target.value)} borderColor="#bb8c52">
                      <option value="Delgada">Delgada</option>
                      <option value="Media">Media</option>
                      <option value="Robusta">Robusta</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Estado de conciencia</FormLabel>
                    <Select value={form.estadoConciencia ?? ''} onChange={e => set('estadoConciencia', e.target.value)} borderColor="#bb8c52">
                      <option value="Consciente">Consciente</option>
                      <option value="Semi consciente">Semi consciente</option>
                      <option value="Inconsciente">Inconsciente</option>
                      <option value="Alterado">Alterado</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Edad estimada</FormLabel>
                    <Input type="number" value={form.edadEstimada ?? (detencion?.edadAlRegistro ?? '')} onChange={e => set('edadEstimada', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Largo de cabello</FormLabel>
                    <Select value={form.largoCabello ?? ''} onChange={e => set('largoCabello', e.target.value)} borderColor="#bb8c52">
                      <option value="Corto">Corto</option>
                      <option value="Mediano">Mediano</option>
                      <option value="Largo">Largo</option>
                      <option value="Rapado">Rapado</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Tipo de cabello</FormLabel>
                    <Select value={form.tipoCabello ?? ''} onChange={e => set('tipoCabello', e.target.value)} borderColor="#bb8c52">
                      <option value="Lacio">Lacio</option>
                      <option value="Ondulado">Ondulado</option>
                      <option value="Rizado">Rizado</option>
                      <option value="Afro / crespo">Afro / crespo</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Color de cabello</FormLabel>
                    <Select value={form.colorCabello ?? ''} onChange={e => set('colorCabello', e.target.value)} borderColor="#bb8c52">
                      <option value="Negro">Negro</option>
                      <option value="Castaño oscuro">Castaño oscuro</option>
                      <option value="Castaño claro">Castaño claro</option>
                      <option value="Rubio">Rubio</option>
                      <option value="Teñido">Teñido</option>
                    </Select>
                  </FormControl>
                  {form.colorCabello === 'Teñido' && (
                    <FormControl>
                      <FormLabel fontSize="sm">Especificar color teñido</FormLabel>
                      <Input value={form.colorCabelloEsp ?? ''} onChange={e => set('colorCabelloEsp', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  )}
                  <FormControl>
                    <FormLabel fontSize="sm">Color de ojos</FormLabel>
                    <Select value={form.colorOjos ?? ''} onChange={e => set('colorOjos', e.target.value)} borderColor="#bb8c52">
                      <option value="Café oscuro">Café oscuro</option>
                      <option value="Café claro">Café claro</option>
                      <option value="Negro">Negro</option>
                      <option value="Verde">Verde</option>
                      <option value="Azul">Azul</option>
                      <option value="Gris">Gris</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Rostro</FormLabel>
                    <Select value={form.rostro ?? ''} onChange={e => set('rostro', e.target.value)} borderColor="#bb8c52">
                      <option value="Ovalado">Ovalado</option>
                      <option value="Redondo">Redondo</option>
                      <option value="Cuadrado">Cuadrado</option>
                      <option value="Alargado">Alargado</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Vello facial</FormLabel>
                    <Select value={form.velloFacial ?? ''} onChange={e => set('velloFacial', e.target.value)} borderColor="#bb8c52">
                      <option value="Sin vello">Sin vello</option>
                      <option value="Bigote">Bigote</option>
                      <option value="Barba completa">Barba completa</option>
                      <option value="Barba de candado">Barba de candado</option>
                      <option value="Sombra de barba">Sombra de barba</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

              {/* ── Señales particulares ─────────────────────────────── */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <HStack><Text fontWeight="semibold" color="#5b182b">Tatuajes</Text><Button size="xs" onClick={agregarTatuaje} leftIcon={<MdAdd />} colorScheme="red" variant="outline">Agregar</Button></HStack>
                  {tatuajes.map((t, i) => (
                    <HStack key={i} align="flex-end" spacing={2}>
                      <FormControl>
                        <FormLabel fontSize="xs">Ubicación</FormLabel>
                        <Input size="sm" value={t.ubicacion} onChange={e => { const c = [...tatuajes]; c[i] = { ...c[i], ubicacion: e.target.value }; setTatuajes(c); }} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs">Descripción</FormLabel>
                        <Input size="sm" value={t.descripcion} onChange={e => { const c = [...tatuajes]; c[i] = { ...c[i], descripcion: e.target.value }; setTatuajes(c); }} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs">Color</FormLabel>
                        <Input size="sm" value={t.color ?? ''} onChange={e => { const c = [...tatuajes]; c[i] = { ...c[i], color: e.target.value }; setTatuajes(c); }} />
                      </FormControl>
                      <IconButton aria-label="Eliminar" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => setTatuajes(prev => prev.filter((_, j) => j !== i))} />
                    </HStack>
                  ))}

                  <Divider />
                  <HStack><Text fontWeight="semibold" color="#5b182b">Cicatrices</Text><Button size="xs" onClick={agregarCicatriz} leftIcon={<MdAdd />} colorScheme="red" variant="outline">Agregar</Button></HStack>
                  {cicatrices.map((c, i) => (
                    <HStack key={i} align="flex-end" spacing={2}>
                      <FormControl>
                        <FormLabel fontSize="xs">Ubicación</FormLabel>
                        <Input size="sm" value={c.ubicacion} onChange={e => { const arr = [...cicatrices]; arr[i] = { ...arr[i], ubicacion: e.target.value }; setCicatrices(arr); }} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs">Tipo</FormLabel>
                        <Select size="sm" value={c.tipo} onChange={e => { const arr = [...cicatrices]; arr[i] = { ...arr[i], tipo: e.target.value }; setCicatrices(arr); }}>
                          <option value="corte">Corte</option>
                          <option value="quemadura">Quemadura</option>
                          <option value="otra">Otra</option>
                        </Select>
                      </FormControl>
                      <IconButton aria-label="Eliminar" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => setCicatrices(prev => prev.filter((_, j) => j !== i))} />
                    </HStack>
                  ))}

                  <Divider />
                  <HStack><Text fontWeight="semibold" color="#5b182b">Piercings</Text><Button size="xs" onClick={agregarPiercing} leftIcon={<MdAdd />} colorScheme="red" variant="outline">Agregar</Button></HStack>
                  {piercings.map((p, i) => (
                    <HStack key={i} align="flex-end">
                      <FormControl>
                        <FormLabel fontSize="xs">Ubicación</FormLabel>
                        <Select size="sm" value={p.ubicacion} onChange={e => { const arr = [...piercings]; arr[i] = { ubicacion: e.target.value }; setPiercings(arr); }}>
                          {['Oreja','Nariz','Ceja','Ombligo','Lengua','Otro'].map(u => <option key={u} value={u}>{u}</option>)}
                        </Select>
                      </FormControl>
                      <IconButton aria-label="Eliminar" icon={<MdDelete />} size="sm" colorScheme="red" variant="ghost" onClick={() => setPiercings(prev => prev.filter((_, j) => j !== i))} />
                    </HStack>
                  ))}
                </VStack>
              </TabPanel>

              {/* ── Estado neurológico ───────────────────────────────── */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Orientación persona</FormLabel>
                    <Textarea value={form.orientacionPersona ?? ''} onChange={e => set('orientacionPersona', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} placeholder="¿Sabe quién es?" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Orientación lugar</FormLabel>
                    <Textarea value={form.orientacionLugar ?? ''} onChange={e => set('orientacionLugar', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} placeholder="¿Sabe dónde está?" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Orientación tiempo</FormLabel>
                    <Textarea value={form.orientacionTiempo ?? ''} onChange={e => set('orientacionTiempo', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} placeholder="¿Sabe la fecha/hora?" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Nivel de cooperación</FormLabel>
                    <Select value={form.nivelCooperacion ?? ''} onChange={e => set('nivelCooperacion', e.target.value)} borderColor="#bb8c52">
                      <option value="Cooperativo">Cooperativo</option>
                      <option value="Agresivo">Agresivo</option>
                      <option value="Pasivo">Pasivo</option>
                      <option value="Confuso">Confuso</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Habla</FormLabel>
                    <Select value={form.habla ?? ''} onChange={e => set('habla', e.target.value)} borderColor="#bb8c52">
                      <option value="Normal">Normal</option>
                      <option value="Incoherente">Incoherente</option>
                      <option value="No verbal">No verbal</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

              {/* ── Signos vitales ───────────────────────────────────── */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Presión arterial (mmHg)</FormLabel>
                    <Input placeholder="120/80" value={form.presionArterial ?? ''} onChange={e => set('presionArterial', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Frec. cardiaca (lpm)</FormLabel>
                    <Input type="number" value={form.frecuenciaCardiaca ?? ''} onChange={e => set('frecuenciaCardiaca', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Frec. respiratoria</FormLabel>
                    <Input type="number" value={form.frecuenciaRespiratoria ?? ''} onChange={e => set('frecuenciaRespiratoria', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Temperatura (°C)</FormLabel>
                    <Input type="number" step="0.1" value={form.temperatura ?? ''} onChange={e => set('temperatura', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Saturación O₂ (SpO2 %)</FormLabel>
                    <Input type="number" step="0.1" value={form.saturacionO2 ?? ''} onChange={e => set('saturacionO2', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                  </FormControl>
                </SimpleGrid>
              </TabPanel>

              {/* ── Condiciones preexistentes ────────────────────────── */}
              <TabPanel>
                <VStack spacing={3} align="stretch">
                  <Text fontWeight="semibold" color="#5b182b">Condiciones reportadas</Text>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                    {(['diabetes','hipertension','epilepsia','asma','enfermedadMental','dependenciaSustancias','embarazo'] as const).map(k => (
                      <Checkbox
                        key={k}
                        isChecked={!!(condiciones as any)[k]}
                        onChange={e => setCondiciones(p => ({ ...p, [k]: e.target.checked }))}
                        colorScheme="red"
                      >
                        {k === 'enfermedadMental' ? 'Enf. mental' : k === 'dependenciaSustancias' ? 'Dep. sustancias' : k.charAt(0).toUpperCase() + k.slice(1)}
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Alergias conocidas</FormLabel>
                      <Textarea value={form.alergias ?? ''} onChange={e => set('alergias', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Medicamentos en uso</FormLabel>
                      <Textarea value={form.medicamentosUso ?? ''} onChange={e => set('medicamentosUso', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* ── Medicamentos administrados ───────────────────────── */}
              <TabPanel>
                <HStack mb={3}>
                  <Text fontWeight="semibold" color="#5b182b">Medicamentos administrados</Text>
                  <Button size="xs" onClick={agregarMed} leftIcon={<MdAdd />} colorScheme="red" variant="outline">Agregar</Button>
                </HStack>
                <VStack spacing={3} align="stretch">
                  {medicamentos.map((m, i) => (
                    <VStack key={i} p={3} border="1px solid" borderColor="gray.200" borderRadius="md" align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" fontWeight="semibold">Medicamento #{i + 1}</Text>
                        <IconButton aria-label="Eliminar" icon={<MdDelete />} size="xs" colorScheme="red" variant="ghost" onClick={() => setMedicamentos(prev => prev.filter((_, j) => j !== i))} />
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs">Nombre</FormLabel>
                          <Input size="sm" value={m.nombre} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], nombre: e.target.value }; setMedicamentos(a); }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">Tipo</FormLabel>
                          <Select size="sm" value={m.tipo} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], tipo: e.target.value }; setMedicamentos(a); }}>
                            {['Analgésico','Sedante','Antibiótico','Antiinflamatorio','Otro'].map(t => <option key={t} value={t}>{t}</option>)}
                          </Select>
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs">Dosis exacta</FormLabel>
                          <Input size="sm" value={m.dosis} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], dosis: e.target.value }; setMedicamentos(a); }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">Vía de administración</FormLabel>
                          <Select size="sm" value={m.via} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], via: e.target.value }; setMedicamentos(a); }}>
                            {['Oral','Intramuscular','Intravenosa','Tópica'].map(v => <option key={v} value={v}>{v}</option>)}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">Hora de administración</FormLabel>
                          <Input size="sm" type="time" value={m.hora} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], hora: e.target.value }; setMedicamentos(a); }} />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel fontSize="xs">Motivo</FormLabel>
                          <Input size="sm" value={m.motivo} onChange={e => { const a = [...medicamentos]; a[i] = { ...a[i], motivo: e.target.value }; setMedicamentos(a); }} />
                        </FormControl>
                      </SimpleGrid>
                    </VStack>
                  ))}
                  {medicamentos.length === 0 && <Text color="gray.400" fontSize="sm">Sin medicamentos administrados</Text>}
                </VStack>
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter borderTopWidth={1}>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} isLoading={saving} onClick={handleGuardar}>
            {existente ? 'Actualizar Control Médico' : 'Guardar Control Médico'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
